import { ColumnDef } from '@tanstack/react-table'
import { SortingHeaderButton } from '../../../../components/ui/DataTable/SortingHeaderButton'
import { RestSchemas } from '@/contexts/QueryProvider'
import { Badge } from '@/components/shadcn/badge'
import { PermissionEditPopover } from './PermissionEditPopover'
import { getEntityTypeName } from '../permissionResourceLabels'

type ConsolidatedPermission = RestSchemas['Messages.Applications.ConsolidatedPermission']

const parsePermissionsJson = (rawValue: string | null | undefined): Record<string, unknown> => {
  if (!rawValue) return {}
  try {
    const parsed = JSON.parse(rawValue) as unknown
    if (Array.isArray(parsed)) {
      const first = parsed[0]
      return typeof first === 'object' && first !== null ? (first as Record<string, unknown>) : {}
    }
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

const formatPermissionLabel = (permissionKey: string) => {
  const strippedPrefix = permissionKey
    .replace(/^(can_|allow_)/, '')
    .replace(/^(can|allow)([A-Z])/, '$2')
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')

  return strippedPrefix
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const appPermissionsTableColumnsV2: ColumnDef<ConsolidatedPermission>[] = [
  {
    id: 'roleName',
    accessorKey: 'roleName',
    header: ({ column }) => <SortingHeaderButton column={column}>Role name</SortingHeaderButton>,
    cell: ({ cell }) => cell.getValue() as string,
    sortingFn: (rowA, rowB) => rowA.original.roleName?.localeCompare(rowB.original.roleName ?? '') ?? 0,
    filterFn: (row, columnId, filterValue: string[]) => {
      if (!Array.isArray(filterValue) || filterValue.length === 0) return true
      const value = row.getValue<string | null | undefined>(columnId) ?? ''
      return filterValue.includes(value)
    },
    enableGlobalFilter: true,
  },
  {
    id: 'configType',
    accessorKey: 'configType',
    header: 'Config Type',
    cell: ({ row }) => {
      const configType = row.original.configType ?? '-'
      const entityTypeName = getEntityTypeName(row.original.configMeta)

      return (
        <div className="flex items-center justify-between gap-3">
          <span>{configType}</span>
          {entityTypeName && <span className="text-muted-foreground">{entityTypeName}</span>}
        </div>
      )
    },
    filterFn: (row, columnId, filterValue: string[]) => {
      if (!Array.isArray(filterValue) || filterValue.length === 0) return true
      const value = row.getValue<string | null | undefined>(columnId) ?? ''
      return filterValue.includes(value)
    },
  },
  {
    id: 'configName',
    accessorKey: 'configName',
    header: 'Config Name',
    cell: ({ row }) => {
      const configName = row.original.configName
      const description = row.original.description

      return (
        <div className="space-y-0.5">
          <div>{configName || '-'}</div>
          {description && <div className="text-xs text-muted-foreground">{description}</div>}
        </div>
      )
    },
    filterFn: (row, columnId, filterValue: string[]) => {
      if (!Array.isArray(filterValue) || filterValue.length === 0) return true
      const value = row.getValue<string | null | undefined>(columnId) ?? ''
      return filterValue.includes(value)
    },
  },
  {
    id: 'permissionsJson',
    accessorKey: 'permissionsJson',
    header: 'Allowed permissions',
    cell: ({ cell }) => {
      const rawValue = cell.getValue() as string | null | undefined
      const parsedPermissions = parsePermissionsJson(rawValue)
      const enabledPermissions = Object.entries(parsedPermissions)
        .filter(([key, isEnabled]) => /^(can_|allow_|can[A-Z]|allow[A-Z])/.test(key) && Boolean(isEnabled))
        .map(([permissionKey]) => formatPermissionLabel(permissionKey))

      return (
        <div className="flex flex-wrap gap-1">
          {enabledPermissions.length > 0 ? (
            enabledPermissions.map((permission) => (
              <Badge key={permission} variant="secondary" className="whitespace-nowrap">
                {permission}
              </Badge>
            ))
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </div>
      )
    },
  },
  {
    id: 'editPermissions',
    header: '',
    enableGlobalFilter: false,
    cell: ({ row }) => (
      <PermissionEditPopover
        item={row.original}
        parsedPermissions={parsePermissionsJson(row.original.permissionsJson ?? undefined)}
      />
    ),
    size: 24,
  },
]
